import React from 'react';
import { Card, Button, Space, Typography, Alert, Spin, Divider, Steps, Tag } from 'antd';
import { WalletOutlined, SafetyOutlined, UserOutlined, KeyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useWeb3Auth } from '../../hooks/useWeb3Auth';

const { Title, Text, Paragraph } = Typography;

/**
 * Web3认证页面组件
 * 处理钱包连接、签名验证和用户注册/登录
 */
const Web3AuthPage: React.FC = () => {
  const {
    isAuthenticated,
    user,
    loading,
    error,
    isWalletConnected,
    walletAddress,
    signAndAuth,
    logout,
    refreshApiKey,
    clearError,
  } = useWeb3Auth();

  // 认证步骤
  const getAuthSteps = () => {
    const steps = [
      {
        title: '连接钱包',
        description: '使用Web3钱包连接',
        icon: <WalletOutlined />,
        status: isWalletConnected ? 'finish' as const : 'wait' as const,
      },
      {
        title: '签名验证',
        description: '签名消息验证身份',
        icon: <SafetyOutlined />,
        status: isWalletConnected && !isAuthenticated ? 'process' as const : isAuthenticated ? 'finish' as const : 'wait' as const,
      },
      {
        title: '完成认证',
        description: '获取访问权限',
        icon: <CheckCircleOutlined />,
        status: isAuthenticated ? 'finish' as const : 'wait' as const,
      },
    ];

    return steps.map((step, index) => ({
      ...step,
      status: loading && index === 1 ? 'process' as const : step.status,
    }));
  };

  // 已认证状态的用户信息展示
  const renderUserInfo = () => (
    <Card title="用户信息" className="mb-6">
      <Space direction="vertical" size="middle" className="w-full">
        <div className="flex items-center justify-between">
          <Text strong>钱包地址:</Text>
          <Tag color="blue">{walletAddress}</Tag>
        </div>
        <div className="flex items-center justify-between">
          <Text strong>用户ID:</Text>
          <Text code>{user?.id}</Text>
        </div>
        <div className="flex items-center justify-between">
          <Text strong>用户名:</Text>
          <Text>{user?.username}</Text>
        </div>
        <div className="flex items-center justify-between">
          <Text strong>注册时间:</Text>
          <Text>{user?.created_at ? new Date(user.created_at).toLocaleString() : '-'}</Text>
        </div>
        <Divider />
        <div className="flex items-center justify-between">
          <Text strong>API密钥:</Text>
          <div className="flex items-center space-x-2">
            <Text code className="max-w-xs truncate">{user?.api_key}</Text>
            <Button 
              size="small" 
              onClick={refreshApiKey}
              loading={loading}
              icon={<KeyOutlined />}
            >
              刷新
            </Button>
          </div>
        </div>
        <Alert
          message="API密钥说明"
          description="API密钥用于外部系统调用通知接口，请妥善保管。如有泄露风险，请及时刷新。"
          type="info"
          showIcon
          className="mt-4"
        />
        <div className="flex justify-center mt-6">
          <Button onClick={logout} size="large">
            退出登录
          </Button>
        </div>
      </Space>
    </Card>
  );

  // 未认证状态的登录界面
  const renderAuthInterface = () => (
    <Card title="Web3身份验证" className="mb-6">
      <Space direction="vertical" size="large" className="w-full">
        <div className="text-center">
          <Title level={3}>
            <UserOutlined className="mr-2" />
            欢迎使用DDDDAlert通知系统
          </Title>
          <Paragraph type="secondary" className="text-base">
            请连接您的Web3钱包并完成签名验证以访问通知管理功能
          </Paragraph>
        </div>

        <Steps 
          current={isWalletConnected ? (isAuthenticated ? 2 : 1) : 0}
          items={getAuthSteps()}
          className="mb-8"
        />

        {error && (
          <Alert
            message="认证失败"
            description={error}
            type="error"
            showIcon
            closable
            onClose={clearError}
            className="mb-4"
          />
        )}

        <div className="space-y-4">
          {!isWalletConnected ? (
            <div className="text-center">
              <Paragraph className="mb-4">
                第一步：连接您的Web3钱包
              </Paragraph>
              <ConnectButton />
            </div>
          ) : !isAuthenticated ? (
            <div className="text-center">
              <Paragraph className="mb-4">
                钱包已连接：<Tag color="green">{walletAddress}</Tag>
              </Paragraph>
              <Paragraph className="mb-4">
                第二步：签名验证身份（不会产生任何费用）
              </Paragraph>
              <Button
                type="primary"
                size="large"
                icon={<SafetyOutlined />}
                onClick={signAndAuth}
                loading={loading}
                className="px-8"
              >
                {loading ? '请在钱包中确认签名...' : '开始签名验证'}
              </Button>
            </div>
          ) : null}
        </div>

        <Alert
          message="安全提示"
          description={
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>签名验证不会产生任何Gas费用</li>
              <li>我们不会存储您的私钥或助记词</li>
              <li>签名仅用于验证钱包所有权</li>
              <li>您可以随时断开连接</li>
            </ul>
          }
          type="info"
          showIcon
          className="mt-6"
        />
      </Space>
    </Card>
  );

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Spin spinning={loading && !error}>
        {isAuthenticated ? renderUserInfo() : renderAuthInterface()}
      </Spin>
    </div>
  );
};

export default Web3AuthPage;
